/**
 * Auto start / auto stop preferences, shared as a module-level singleton so the
 * recorder and every page read the same live value.
 */

import { computed, ref } from 'vue';
import type { AutoStartMode } from '@/lib/autoRecordDetector';
import {
  AUTO_RECORD_STORAGE_KEY,
  DEFAULT_AUTO_RECORD_PREFERENCES,
  autoRecordConfigFrom,
  parseAutoRecordPreferences,
  serializeAutoRecordPreferences,
  type AutoRecordPreferences,
} from '@/lib/autoRecordSettings';

const preferences = ref<AutoRecordPreferences>({ ...DEFAULT_AUTO_RECORD_PREFERENCES });

function readStored(): AutoRecordPreferences {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_AUTO_RECORD_PREFERENCES };
  try {
    return parseAutoRecordPreferences(localStorage.getItem(AUTO_RECORD_STORAGE_KEY));
  } catch {
    return { ...DEFAULT_AUTO_RECORD_PREFERENCES };
  }
}

function persist(next: AutoRecordPreferences): void {
  preferences.value = next;
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(AUTO_RECORD_STORAGE_KEY, serializeAutoRecordPreferences(next));
  } catch {
    // Storage may be full or blocked — the in-memory preference still applies.
  }
}

/** Load preferences from storage. Call once before app mount (e.g. in main.ts). */
export function initAutoRecord(): void {
  preferences.value = readStored();
}

/** Current detector config — read by the recorder on every sample. */
export function getAutoRecordConfig() {
  return autoRecordConfigFrom(preferences.value);
}

export function useAutoRecord() {
  const startMode = computed(() => preferences.value.startMode);
  const autoStop = computed(() => preferences.value.autoStop);

  function setStartMode(mode: AutoStartMode): void {
    persist({ ...preferences.value, startMode: mode });
  }

  function setAutoStop(enabled: boolean): void {
    persist({ ...preferences.value, autoStop: enabled });
  }

  return { startMode, autoStop, setStartMode, setAutoStop };
}
